<template>
  <div class="flex flex-column gap-4">
    <div class="flex justify-content-between align-items-center">
      <h3 class="m-0">Uncompleted Task</h3>
      <div class="flex gap-2 align-items-center">
        <a-button class="flex align-items-center justify-content-center" @click="loadData" title="Refresh Page">
          <a-icon width="20" height="20" icon="solar:refresh-square-broken" />
        </a-button>
      </div>
    </div>

    <a-radio-group class="w-fit" v-model="type" button-style="solid">
      <a-radio-button :value="undefined"> All </a-radio-button>
      <a-radio-button value="warning"> Warning </a-radio-button>
      <a-radio-button value="forbid_work"> Critical </a-radio-button>
    </a-radio-group>

    <a-list item-layout="vertical" :data-source="filteredTasks" :bordered="true" :loading="loading">
      <div slot="header">Total: {{ filteredTasks.length }}</div>

      <a-list-item slot="renderItem" slot-scope="item" class="pr-2 flex flex-column gap-2">
        <div class="flex justify-content-between">
          <h4>
            {{ item.title }}
          </h4>
          <a-tag class="h-fit m-0" :color="getSeverity(item.severity).color">
            {{ getSeverity(item.severity).title }}
          </a-tag>
        </div>
        <p>{{ item.details }}</p>
        <div class="flex align-items-center justify-content-between gap-2">
          <p><b>Created Date: </b>{{ formatDate(item.created_at) }}</p>
          <a-button
            v-if="
              hasUncontrolledPermission('manage_authorizations', {
                id: selectedAssociateId
              })
            "
            @click="deleteSelectedAlert(item.id)"
            class="h-fit p-0"
            type="link"
          >
            Delete
          </a-button>
        </div>
      </a-list-item>
    </a-list>
  </div>
</template>

<script>
import permissionMixin from '@/mixins/permissionMixin'
import util from '@/util'
import { Button, Radio, Tag, List, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions, mapGetters } = createNamespacedHelpers('alerts')
const { mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-tag': Tag,
    'a-radio-group': Radio.Group,
    'a-radio-button': Radio.Button,
    'a-list': List,
    'a-list-item': List.Item
  },
  mixins: [permissionMixin],
  props: {
    associateId: {
      type: [Number, String],
      default: undefined
    },
    initialSeverity: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      loading: false,
      data: undefined,
      type: this.initialSeverity
    }
  },
  computed: {
    ...profileState({
      associate: state => state.associate
    }),
    ...mapGetters(['getSeverity', 'getType']),

    filteredTasks() {
      return (this.data?.tasks ?? []).filter(item => (this.type ? item.severity === this.type : true))
    },
    selectedAssociateId() {
      return this.associateId ?? this.associate?.id
    }
  },
  watch: {
    initialSeverity(value) {
      this.type = value
    },
    selectedAssociateId() {
      this.loadData()
    }
  },
  methods: {
    ...mapActions(['getAssociateUncompletedTasks', 'deleteAlert']),
    ...modalActions(['openModal']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    formatDate(date) {
      return util.convertDateTimeToCurrentTimeZoneWithFormat(date, 'MM/DD/YYYY')
    },

    async loadData() {
      this.loading = true

      const payload = {
        associate_id: this.selectedAssociateId
      }

      try {
        const data = await this.getAssociateUncompletedTasks(payload)

        this.data = data
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error.message)
      } finally {
        this.loading = false
      }
    },

    async deleteSelectedAlert(alert_id) {
      this.openModal({
        type: 'deletion',
        data: {
          visible: true,
          title: 'Delete Uncompleted Task?',
          description: 'Are you sure you want to delete this Uncompleted Task? ',
          onConfirm: async () => {
            await this.deleteAlert({ alert_id })

            this.data.tasks = this.data.tasks.filter(item => item.id !== alert_id)
            this.$emit('updated')
          }
        }
      })
    }
  },
  async mounted() {
    if (this.selectedAssociateId) await this.loadData()
  }
}
</script>

<style scoped>
::v-deep .ant-list-items {
  overflow: auto;
  max-height: 500px;
}

::v-deep .ant-spin-nested-loading {
  min-height: 100px;
}
</style>
